import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { ClienteCentroCustoLista } from "../../domain/models/clienteCentroCustoLista";
import { ClienteArquivoLista } from "../../domain/models/clienteArquivoLista";
import { ClientesForm, ClientesSchema, defaultClientesForm } from "../schemas/Clientes_cadastro/clientesCadastro";
import { ClienteCentroCustoForm } from "../schemas/Clientes_cadastro/clienteCentroCustoCadastro";
import { ClienteArquivoForm } from "../schemas/Clientes_cadastro/clienteArquivoCadastro";
import { Cliente } from "../../domain/models/cliente";
import { ClienteCentroCusto } from "../../domain/models/clienteCentroCusto";
import { ClienteArquivo } from "../../domain/models/clienteArquivo";
import { useClienteCadastroPagination } from "../hooks/cliente_cadastro/pagination";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";

export const useClientesCadastroViewModel = () => {
    const {
        rh: {
            getCepUseCase,
        },
        commons: {
            getCentroCustoDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,

        },
        comercial: {
            getClienteByIdUseCase,
            getClienteCentroCustoByIdUseCase,
            getAllClienteCentroCustoUseCase,
            getAllClienteArquivoUseCase,
            getClienteArquivoByIdUseCase,
            deleteClienteUseCase,
            deleteClienteArquivoUseCase,
            deleteClienteCentroCustoUseCase,
            createClienteUseCase,
            updateClienteUseCase,
            createClienteArquivoUseCase,
            createClienteCentroCustoUseCase,
            updateClienteArquivoUseCase,
            updateClienteCentroCustoUseCase,
            downloadClienteArquivoUseCase,
        },


    } = useDependencyInjection();

    const location = useLocation();
    const navigate = useNavigate();

    const onLoadCepSkiper = useRef(false);

    const debouncedReff = useCreateDebounceHandler(750);

    const menuRef = useRef<HTMLButtonElement>(null);
    const pagination = useClienteCadastroPagination();

    const [openMenu, setOpenMenu] = useState(false);


    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const { pushErrorNotification, pushInfoNotification, setCustomLoading } = usePageLoader();

    const [clienteSelectedId, setClienteSelectedId] = useState<number | undefined>();
    const [clienteArquivoSelectedIdDelete, setClienteArquivoSelectedIdDelete] = useState<number | undefined>();
    const [clienteCentroCustoSelectedIdDelete, setClienteCentroCustoSelectedIdDelete] = useState<number | undefined>();


    const [cliente, setCliente] = useState<Cliente | undefined>();
    const [clienteCentroCusto, setClienteCentroCusto] = useState<ClienteCentroCusto | undefined>();
    const [clienteArquivo, setClienteArquivo] = useState<ClienteArquivo | undefined>();


    const [clienteCentroCustoLista, setClienteCentroCustoLista] = useState<ClienteCentroCustoLista[]>([]);
    const [clienteArquivoLista, setClienteArquivoLista] = useState<ClienteArquivoLista[]>([]);


    // MODAIS
    const [openDeleteClienteModal, setOpenDeleteClienteModal] = useState<boolean>(false);
    const [openDeleteClienteCentroCustoModal, setOpenDeleteClienteCentroCustoModal] = useState<boolean>(false);
    const [openDeleteClienteArquivoModal, setOpenDeleteClienteArquivoModal] = useState<boolean>(false);

    const [openDeleteArquivoModal, setOpenDeleteArquivoModal] = useState<boolean>(false);

    const [openClienteCentroCustoModal, setClienteCentroCustoModal] = useState<boolean>(false);
    const [openClienteArquivoModal, setClienteArquivoModal] = useState<boolean>(false);

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

    const hookFormCliente = useForm<ClientesForm>({
        reValidateMode: "onSubmit",
        mode: "onSubmit",
        defaultValues: defaultClientesForm,
        resolver: zodResolver(ClientesSchema),
    });


    //#region CARREGAMENTO

    const loadCep = useCallback(async (cep: string) => {
        const normalizedCep = cep.trim();
        const requiredSize = 'xx.xxx-xxx';

        if (normalizedCep.length === requiredSize.length) {
            const cepInfo = await getCepUseCase.getCep(cep);
            if (cepInfo) {
                hookFormCliente.setValue('rua', cepInfo.logradouro.toUpperCase());
                hookFormCliente.setValue('bairro', cepInfo.bairro.toUpperCase());
                hookFormCliente.setValue('cidade', cepInfo.cidade.toUpperCase());
                hookFormCliente.setValue('uf', cepInfo.uf.toUpperCase());
                hookFormCliente.setValue('pais', cepInfo.pais.toUpperCase());
            }
        }
        else if (normalizedCep.length === 0) {
            hookFormCliente.setValue('rua', '');
            hookFormCliente.setValue('bairro', '');
            hookFormCliente.setValue('cidade', '');
            hookFormCliente.setValue('uf', '');
            hookFormCliente.setValue('pais', '');
        }

    }, [
        getCepUseCase,
        hookFormCliente,
    ]);

    const loadAllClienteCentroCusto = useCallback(async (idCliente: number, currentPage?: number, rowsPerPage?: number) => {
        try {
            await getAllClienteCentroCustoUseCase.execute(idCliente, currentPage, rowsPerPage);
            setClienteCentroCustoLista(getAllClienteCentroCustoUseCase.items);
        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro de centro de custo do cliente!");
        }
    }, [
        getAllClienteCentroCustoUseCase,
        pushErrorNotification
    ]);

    const loadAllClienteArquivo = useCallback(async (idCliente: number, currentPage?: number, rowsPerPage?: number) => {
        try {
            await getAllClienteArquivoUseCase.execute(idCliente, currentPage, rowsPerPage);
            setClienteArquivoLista(getAllClienteArquivoUseCase.items);

        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro de arquivos do cliente!");
        }
    }, [
        getAllClienteArquivoUseCase,
        pushErrorNotification
    ]);

    const loadClienteCentroCusto = useCallback(async (idClienteCentroCusto: number) => {
        try {
            const clienteCC = await getClienteCentroCustoByIdUseCase.execute(idClienteCentroCusto);
            setClienteCentroCusto(clienteCC);

        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro de centro de custo!");
        }
    }, [
        getClienteCentroCustoByIdUseCase,
        pushErrorNotification]);

    const loadCliente = useCallback(async (idCliente: number) => {
        try {
            const cliente = await getClienteByIdUseCase.execute(idCliente);
            for (const [key, value] of Object.entries(cliente)) {
                hookFormCliente.setValue(key as any, value ?? null);
            }
            setCliente(cliente);

            await loadAllClienteCentroCusto(idCliente);
            await loadAllClienteArquivo(idCliente);
        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro do cliente!");
        }
    }, [
        getClienteByIdUseCase,
        hookFormCliente,
        loadAllClienteCentroCusto,
        loadAllClienteArquivo,
        pushErrorNotification
    ]);

    const loadClienteArquivo = useCallback(async (idClienteArquivo: number) => {
        try {
            const clienteArquivo = await getClienteArquivoByIdUseCase.execute(idClienteArquivo);
            setClienteArquivo(clienteArquivo);
        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro de arquivo!");
        }
    }, [
        getClienteArquivoByIdUseCase,
        pushErrorNotification]);



    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar centro de custo!"));
        }
    }, [
        getCentroCustoDropDownUseCase,
        pushErrorNotification
    ]);


    //#endregion




    //#region  PAGE START
    usePageStartBootstrap(async () => {

        await loadCentroCustoDropDown();
        const idCliente = clienteSelectedId ?? (location.state?.idClientes as number);
        if (idCliente) {
            setClienteSelectedId(idCliente);
            await loadCliente(idCliente);
        } else {
            hookFormCliente.reset();
            setCliente(undefined);
            setClienteSelectedId(undefined);
        }
    });

    useEffect(() => {
        onLoadCepSkiper.current = true;

    }, []);

    //#endregion





    //#region CABEÇALHO

    const handlePageBack = useCallback(() => {
        navigate('/Clientes_lista')
    }, [navigate]);


    const handleNewClientes = useCallback(() => {
        hookFormCliente.reset();
        setCliente(undefined);
        setClienteSelectedId(undefined);
        setClienteCentroCustoLista([]);
        setClienteArquivoLista([]);

    }, [hookFormCliente]);


    const handleCloseMenu = useCallback((event: any) => {
        if (
            menuRef.current &&
            menuRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setOpenMenu(false);
    }, []);

    const handleToggleMenu = useCallback(() => {
        setOpenMenu((prev) => !prev);
    }, []);

    const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpenMenu(false);
        } else if (event.key === 'Escape') {
            setOpenMenu(false);
        }
    }, []);

    //#endregion




    //#region TABELAS ARQUIVOS / CENTRO CUSTO

    const handleClienteCentroCustoPageChange = useCallback((newPage: number) => {
        loadAllClienteCentroCusto(clienteSelectedId!, newPage + 1, pagination.clienteCentroCusto.rowsPerPage);
    }, [
        loadAllClienteCentroCusto,
        clienteSelectedId,
        pagination.clienteCentroCusto.rowsPerPage
    ]);


    const handleClienteCentroCustoRowsPerPageChange = useCallback((newRowsPerPage: number) => {
        loadAllClienteCentroCusto(clienteSelectedId!, pagination.clienteCentroCusto.page, newRowsPerPage);

    }, [
        loadAllClienteCentroCusto,
        clienteSelectedId,
        pagination.clienteCentroCusto.page
    ]);


    const handleClienteArquivoPageChange = useCallback((newPage: number) => {
        loadAllClienteArquivo(clienteSelectedId!, newPage + 1, pagination.clienteArquivos.rowsPerPage);
    }, [
        loadAllClienteArquivo,
        clienteSelectedId,
        pagination.clienteArquivos.rowsPerPage
    ]);


    const handleClienteArquivoRowsPerPageChange = useCallback((newRowsPerPage: number) => {
        loadAllClienteArquivo(clienteSelectedId!, pagination.clienteArquivos.page, newRowsPerPage);
    }, [
        loadAllClienteArquivo,
        clienteSelectedId,
        pagination.clienteArquivos.page
    ]);


    //#endregion





    //#region SALVAR/UPDATE

    const handleSalvarClientes = useCallback(async (form: ClientesForm) => {
        try {

            setSaving(true);
            setLoading(true);
            if (clienteSelectedId) {
                await updateClienteUseCase.execute({
                    idCliente: clienteSelectedId,
                    razaoSocial: form.razaoSocial ?? undefined,
                    nomeFantasia: form.nomeFantasia ?? undefined,
                    cnpj: form.cnpj ?? undefined,
                    cpf: form.cpf ?? undefined,
                    incricaoEstadual: form.incricaoEstadual ?? undefined,
                    incricaoMunicipal: form.incricaoMunicipal ?? undefined,
                    atividade: form.atividade ?? undefined,
                    obsCliente: form.obsCliente ?? undefined,
                    idEndereco: form.idEndereco ?? undefined,
                    telefoneFixo: form.telefoneFixo ?? undefined,
                    telefoneCelular: form.telefoneCelular ?? undefined,
                    email: form.email ?? undefined,
                    contato: form.contato ?? undefined,
                    unidade: form.unidade ?? undefined,
                    dataNascimento: form.dataNascimento ?? undefined,
                    nomeFantasia2: form.nomeFantasia2 ?? undefined,
                    cpf2: form.cpf2 ?? undefined,
                    dataNascimento2: form.dataNascimento2 ?? undefined,

                    // endereco
                    rua: form.rua ?? undefined,
                    numero: form.numero ?? undefined,
                    apartamento: form.apartamento ?? undefined,
                    cep: form.cep ?? undefined,
                    uf: form.uf ?? undefined,
                    cidade: form.cidade ?? undefined,
                    bairro: form.bairro ?? undefined,
                    pais: form.pais ?? undefined,
                });

                await loadCliente(clienteSelectedId);
                setClienteSelectedId(clienteSelectedId);
                pushInfoNotification("Sucesso", "Cliente atualizado com sucesso!");
            } else {
                const idCliente = await createClienteUseCase.execute({
                    idCliente: form.idCliente ?? 0,
                    dataCadastro: form.dataCadastro,
                    razaoSocial: form.razaoSocial ?? undefined,
                    nomeFantasia: form.nomeFantasia ?? undefined,
                    cnpj: form.cnpj ?? undefined,
                    cpf: form.cpf ?? undefined,
                    incricaoEstadual: form.incricaoEstadual ?? undefined,
                    incricaoMunicipal: form.incricaoMunicipal ?? undefined,
                    atividade: form.atividade ?? undefined,
                    obsCliente: form.obsCliente ?? undefined,
                    idEndereco: form.idEndereco ?? undefined,
                    telefoneFixo: form.telefoneFixo ?? undefined,
                    telefoneCelular: form.telefoneCelular ?? undefined,
                    email: form.email ?? undefined,
                    contato: form.contato ?? undefined,
                    unidade: form.unidade ?? undefined,
                    dataNascimento: form.dataNascimento ?? undefined,
                    nomeFantasia2: form.nomeFantasia2 ?? undefined,
                    cpf2: form.cpf2 ?? undefined,
                    dataNascimento2: form.dataNascimento2 ?? undefined,

                    // endereco
                    rua: form.rua ?? undefined,
                    numero: form.numero ?? undefined,
                    apartamento: form.apartamento ?? undefined,
                    cep: form.cep ?? undefined,
                    uf: form.uf ?? undefined,
                    cidade: form.cidade ?? undefined,
                    bairro: form.bairro ?? undefined,
                    pais: form.pais ?? undefined,
                });

                await loadCliente(idCliente);
                setClienteSelectedId(idCliente);
                pushInfoNotification("Sucesso", "Cliente salvo com sucesso!");
            }


        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar cliente!"));
        } finally {
            setSaving(false);
            setLoading(false);
        }
    }, [createClienteUseCase,
        updateClienteUseCase,
        clienteSelectedId,
        loadCliente,
        pushInfoNotification,
        pushErrorNotification]);




    const handleSalvarClienteCentroCusto = useCallback(async (form: ClienteCentroCustoForm) => {

        try {
            setSaving(true);
            setLoading(true);

            if (!clienteSelectedId) {
                pushErrorNotification("Erro", "Cliente não selecionado para adicionar centro de custo!");
                return;
            }

            if (form.idClienteCentroCusto && form.idClienteCentroCusto > 0) {
                await updateClienteCentroCustoUseCase.execute({
                    idClienteCentroCusto: form.idClienteCentroCusto ?? 0,
                    idCliente: clienteSelectedId,
                    idCentroCusto: form.idCentroCusto!,
                    principal: form.principal ? 1 : 0,
                    unidade: form.unidade ?? undefined,
                });


            } else {
                await createClienteCentroCustoUseCase.execute({
                    idClienteCentroCusto: form.idClienteCentroCusto ?? 0,
                    idCliente: clienteSelectedId,
                    idCentroCusto: form.idCentroCusto!,
                    principal: form.principal ? 1 : 0,
                    unidade: form.unidade ?? undefined,
                });
            }

            await loadAllClienteCentroCusto(clienteSelectedId!);
            pushInfoNotification("Sucesso", "Centro de custo do cliente salvo com sucesso!");
            setClienteCentroCustoModal(false);

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar Cliente centro de custo!"));
        } finally {
            setSaving(false);
            setLoading(false);
        }
    }, [
        createClienteCentroCustoUseCase,
        updateClienteCentroCustoUseCase,
        clienteSelectedId,
        loadAllClienteCentroCusto,
        pushInfoNotification,
        pushErrorNotification
    ]);


    const handleSalvarClienteArquivo = useCallback(async (form: ClienteArquivoForm) => {
        try {
            setSaving(true);
            if (!clienteSelectedId) {
                pushErrorNotification("Erro", "Cliente não selecionado para adicionar arquivo!");
                return;
            }

            if (form.idClienteArquivo && form.idClienteArquivo > 0) {
                await updateClienteArquivoUseCase.execute(
                    form.idClienteArquivo,
                    clienteSelectedId,
                    form.descricao ?? undefined,
                    form.arquivo ? form.arquivo : undefined,
                    form.limparArquivo ?? undefined,
                );

            } else {
                await createClienteArquivoUseCase.execute(
                    clienteSelectedId,
                    form.descricao ?? undefined,
                    form.arquivo ? form.arquivo : undefined,
                );
            }
            await loadAllClienteArquivo(clienteSelectedId!);
            pushInfoNotification("Sucesso", "Arquivo do cliente salvo com sucesso!");
            setClienteArquivoModal(false);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar arquivo do cliente!"));
        } finally {
            setSaving(false);
        }
    }, [
        createClienteArquivoUseCase,
        updateClienteArquivoUseCase,
        clienteSelectedId,
        loadAllClienteArquivo,
        pushInfoNotification,
        pushErrorNotification
    ]);
    //#endregion




    //#region MODAIS ARQUIVOS /CENTRO CUSTO


    const handleClienteCentroCustoModalOpenState = useCallback(async (idClienteCentroCusto: number | null, openState: boolean) => {
        if (openState && idClienteCentroCusto) {
            await loadClienteCentroCusto(idClienteCentroCusto);
            setClienteCentroCustoModal(openState)
        } else {
            setClienteCentroCustoModal(openState)
            setClienteCentroCusto(undefined);
        }
    }, [
        loadClienteCentroCusto,
    ]);

    const handleClienteArquivoModalOpenState = useCallback(async (idClienteArquivo: number | null, openState: boolean) => {
        if (openState && idClienteArquivo) {
            await loadClienteArquivo(idClienteArquivo);
            setClienteArquivoModal(openState)
        } else {
            setClienteArquivoModal(openState)
            setClienteArquivo(undefined);
        }
    }, [
        loadClienteArquivo,
    ]);




    //#endregion




    //#region DELEÇÃO
    const handleClienteDeleteOpenState = useCallback((openState: boolean) => {
        if (openState && clienteSelectedId) {
            setOpenDeleteClienteModal(openState)
        } else {
            setOpenDeleteClienteModal(openState)
        }
    }, [clienteSelectedId]);

    const handleClienteCentroCustoDeleteOpenState = useCallback((idClienteCentroCusto: number | null, openState: boolean) => {
        if (openState && idClienteCentroCusto) {
            setOpenDeleteClienteCentroCustoModal(openState)
            setClienteCentroCustoSelectedIdDelete(idClienteCentroCusto)
        } else {
            setOpenDeleteClienteCentroCustoModal(openState)
            setClienteCentroCustoSelectedIdDelete(undefined)
        }
    }, []);

    const handleClienteArquivoDeleteOpenState = useCallback((idClienteArquivo: number | null, openState: boolean) => {
        if (openState && idClienteArquivo) {
            setOpenDeleteClienteArquivoModal(openState)
            setClienteArquivoSelectedIdDelete(idClienteArquivo)
        } else {
            setOpenDeleteClienteArquivoModal(openState)
            setClienteArquivoSelectedIdDelete(undefined)
        }
    }, []);


    const handleConfirmarDeletarCliente = useCallback(async () => {
        try {
            if (clienteSelectedId) {
                await deleteClienteUseCase.execute(clienteSelectedId);
                pushInfoNotification("Sucesso", "Cliente deletado com sucesso!");
                navigate("/Clientes_lista");
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar cliente!"));
        }
    }, [clienteSelectedId,
        deleteClienteUseCase,
        pushInfoNotification,
        pushErrorNotification,
        navigate]);

    const handleConfirmarDeletarClienteCentroCusto = useCallback(async () => {
        try {
            if (clienteCentroCustoSelectedIdDelete) {
                await deleteClienteCentroCustoUseCase.execute(clienteCentroCustoSelectedIdDelete);
                setClienteCentroCustoSelectedIdDelete(undefined);
                setOpenDeleteClienteCentroCustoModal(false);

                pushInfoNotification("Sucesso", "Centro de custo do cliente deletado com sucesso!");
                await loadAllClienteCentroCusto(clienteSelectedId!);
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar centro de custo do cliente!"));
        }
    }, [
        clienteCentroCustoSelectedIdDelete,
        clienteSelectedId,
        deleteClienteCentroCustoUseCase,
        loadAllClienteCentroCusto,
        pushInfoNotification,
        pushErrorNotification,
    ]);

    const handleConfirmarDeletarClienteArquivo = useCallback(async () => {
        try {
            if (clienteArquivoSelectedIdDelete) {
                await deleteClienteArquivoUseCase.execute(clienteArquivoSelectedIdDelete);
                setClienteArquivoSelectedIdDelete(undefined);
                setOpenDeleteClienteArquivoModal(false);
                pushInfoNotification("Sucesso", "Arquivo do cliente deletado com sucesso!");
                await loadAllClienteArquivo(clienteSelectedId!);
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar arquivo do cliente!"));
        }
    }, [
        clienteArquivoSelectedIdDelete,
        deleteClienteArquivoUseCase,
        clienteSelectedId,
        pushInfoNotification,
        pushErrorNotification,
        loadAllClienteArquivo,
    ]);


    //#endregion


    const handleLoadCep = useDebouncedCallback(debouncedReff, (cep: string) => {
        return async () => {
            try {
                await loadCep(cep);
            } catch (e: unknown) {
                pushErrorNotification("Erro", processError(e, "Falha ao carregar CEP!"));
            }
        }
    }, [
        loadCep,
        pushErrorNotification,
    ]);

    //#region ARQUIVOS

    const handleDownloadClienteArquivo = useCallback(async (idClienteArquivo: number, nomeArquivo: string) => {
        try {
            setCustomLoading(true, "Baixando arquivo...");
            await downloadClienteArquivoUseCase.execute(idClienteArquivo, nomeArquivo);

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao baixar arquivo!"));
        } finally {
            setCustomLoading(false);
        }
    }, [
        downloadClienteArquivoUseCase,
        setCustomLoading,
        pushErrorNotification,
    ]);

    const handleArquivoDeleteOpenState = useCallback((limparArquivos: string | null, arquivo: string | null, openState: boolean) => {
        if (openState && limparArquivos && arquivo) {
            setOpenDeleteArquivoModal(openState)
        } else {
            setOpenDeleteArquivoModal(openState)
        }
    }, []);


    const handleDeleteFile = useCallback(() => {
        setClienteArquivo(prevState => {
            if (prevState === undefined) return prevState;
            return { ...prevState, nome: undefined, arquivo: undefined, limparArquivo: true };
        });
    }, [
    ]);

    //#endregion



    //#region HISTORICO

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(cliente?.idCliente ?? 0, LogTipoTabela.CLIENTE, true);
            }
        }
    }, [
        cliente,
        changeHistory,
    ]);

    //#endregion



    return {
        hookFormCliente,
        cliente,
        saving,
        loading,
        clienteCentroCusto,
        clienteArquivo,
        clienteCentroCustoLista,
        clienteArquivoLista,
        openDeleteClienteModal,
        openDeleteClienteCentroCustoModal,
        openDeleteClienteArquivoModal,
        openClienteCentroCustoModal,
        openClienteArquivoModal,
        clienteSelectedId,
        ddlCentroCusto,
        clienteCentroCustoSelectedIdDelete,
        clienteArquivoSelectedIdDelete,
        openDeleteArquivoModal,
        changeHistory,
        openMenu,
        menuRef,
        pagination,
        onLoadCepSkiper,

        handleLoadCep,
        handleDeleteFile,
        handleDownloadClienteArquivo,
        handleClienteCentroCustoPageChange,
        handleClienteCentroCustoRowsPerPageChange,
        handleClienteArquivoPageChange,
        handleClienteArquivoRowsPerPageChange,
        handleClienteArquivoDeleteOpenState,
        handleClienteCentroCustoModalOpenState,
        handleClienteArquivoModalOpenState,
        handleOpenChangeHistory,
        handleSalvarClientes: hookFormCliente.handleSubmit(handleSalvarClientes),
        handleSalvarClienteCentroCusto,
        handleSalvarClienteArquivo,
        handleClienteDeleteOpenState,
        handleClienteCentroCustoDeleteOpenState,
        handleConfirmarDeletarClienteArquivo,
        handleConfirmarDeletarCliente,
        handleConfirmarDeletarClienteCentroCusto,
        handleArquivoDeleteOpenState,
        handlePageBack,
        handleCloseMenu,
        handleListKeyDown,
        handleToggleMenu,
        handleNewClientes,

    };
};



